import { useQuery } from '@apollo/client';
import {
  extractBodyPreview,
  extractTemplateVariableLabels,
} from 'lib/meta/whatsapp-templates';
import {
  type WhatsAppComposePayload,
  buildDefaultTemplateVariables,
  interpolateTemplateBody,
} from 'lib/missive/whatsapp-compose';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';

import { getWhatsAppTemplates } from 'queries/whatsapp-automation';

import { useSession } from 'providers/session';

const Modal = dynamic(() => import('components/molecules/modal'));

type WhatsAppTemplateOption = {
  id: string;
  meta_template_id: string | null;
  meta_template_name: string;
  status: string;
  body_preview: string | null;
  variable_labels: Array<{ index: string; label: string }>;
  components: Array<{ type: string; text?: string }> | null;
};

type LeadWhatsAppComposeModalProps = {
  open: boolean;
  phone: string;
  leadName?: string;
  onClose: () => void;
  onSubmit: (payload: WhatsAppComposePayload) => void;
};

function getTemplateBody(template: WhatsAppTemplateOption | null) {
  if (!template) {
    return '';
  }

  return (
    extractBodyPreview(template.components || undefined) ||
    template.body_preview ||
    ''
  );
}

export default function LeadWhatsAppComposeModal({
  open,
  phone,
  leadName,
  onClose,
  onSubmit,
}: LeadWhatsAppComposeModalProps) {
  const { token } = useSession();
  const [mode, setMode] = useState<'template' | 'freeform'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [freeformBody, setFreeformBody] = useState('Hi');
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);

  const { data, loading } = useQuery(getWhatsAppTemplates, {
    variables: {
      where: { status: { _eq: 'APPROVED' } },
    },
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const templates: WhatsAppTemplateOption[] = data?.whatsapp_template || [];

  const selectedTemplate = useMemo(
    () =>
      templates.find((template) => template.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const templateBody = getTemplateBody(selectedTemplate);
  const variableLabels = useMemo(() => {
    if (selectedTemplate?.variable_labels?.length) {
      return selectedTemplate.variable_labels;
    }

    return extractTemplateVariableLabels(templateBody);
  }, [selectedTemplate, templateBody]);

  useEffect(() => {
    if (!open || !token || !phone.trim()) {
      return;
    }

    setSessionLoading(true);

    fetch(
      `/api/missive/whatsapp-session?phone=${encodeURIComponent(phone.trim())}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then((response) => response.json())
      .then((payload) => {
        setSessionOpen(Boolean(payload?.isOpen));
      })
      .catch(() => {
        setSessionOpen(false);
      })
      .finally(() => {
        setSessionLoading(false);
      });
  }, [open, phone, token]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [open, templates, selectedTemplateId]);

  useEffect(() => {
    if (!open || !selectedTemplate) {
      return;
    }

    setVariables(buildDefaultTemplateVariables(variableLabels, leadName));
  }, [open, selectedTemplate?.id, leadName, variableLabels]);

  useEffect(() => {
    if (!open) {
      setMode('template');
      setSelectedTemplateId('');
      setVariables({});
      setFreeformBody('Hi');
    }
  }, [open]);

  const preview = useMemo(() => {
    if (mode === 'freeform') {
      return freeformBody;
    }

    if (!templateBody) {
      return '';
    }

    return interpolateTemplateBody(templateBody, variables);
  }, [mode, freeformBody, templateBody, variables]);

  const missingVariables = variableLabels.filter(
    (variable) => !variables[variable.index]?.trim(),
  );

  const canSubmit =
    mode === 'freeform'
      ? sessionOpen && freeformBody.trim().length > 0
      : Boolean(selectedTemplate?.meta_template_id) &&
        missingVariables.length === 0;

  function handleSubmit() {
    if (!canSubmit) {
      return;
    }

    if (mode === 'freeform') {
      onSubmit({ freeformBody: freeformBody.trim() });
      return;
    }

    if (!selectedTemplate?.meta_template_id) {
      return;
    }

    onSubmit({
      templateId: selectedTemplate.meta_template_id,
      templateBody,
      templateVariables: variables,
    });
  }

  return (
    <Modal
      open={open}
      close={onClose}
      title="WhatsApp in Missive"
      subTitle={`Send to ${phone}`}
      small={false}
      large
      overflow="overflow-hidden"
    >
      <div className="mt-4 flex max-h-[calc(100vh-11rem)] min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-4 pb-4">
            {sessionLoading ? (
              <p className="text-sm text-gray-500">
                Checking WhatsApp session…
              </p>
            ) : sessionOpen ? (
              <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                24-hour WhatsApp session is open. You can send a free-form
                message or use a template.
              </p>
            ) : (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                24-hour session is closed. Choose an approved template to start
                the conversation.
              </p>
            )}

            {sessionOpen ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('template')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    mode === 'template'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  Template
                </button>
                <button
                  type="button"
                  onClick={() => setMode('freeform')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    mode === 'freeform'
                      ? 'bg-purple-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700'
                  }`}
                >
                  Free-form
                </button>
              </div>
            ) : null}

            {mode === 'freeform' ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  value={freeformBody}
                  onChange={(event) => setFreeformBody(event.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Template
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(event) =>
                      setSelectedTemplateId(event.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    disabled={loading || templates.length === 0}
                  >
                    {templates.length === 0 ? (
                      <option value="">No approved templates</option>
                    ) : (
                      templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.meta_template_name}
                        </option>
                      ))
                    )}
                  </select>
                  {templates.length === 0 && !loading ? (
                    <p className="mt-2 text-xs text-amber-700">
                      Sync approved templates from Templates → WhatsApp first.
                    </p>
                  ) : null}
                </div>

                {variableLabels.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">
                      Variables
                    </p>
                    {variableLabels.map((variable) => (
                      <div key={variable.index}>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          {variable.label}
                        </label>
                        <input
                          type="text"
                          value={variables[variable.index] || ''}
                          onChange={(event) =>
                            setVariables((current) => ({
                              ...current,
                              [variable.index]: event.target.value,
                            }))
                          }
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            {preview ? (
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Preview
                </p>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap text-gray-800">
                  {preview}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-gray-100 bg-white pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Open in Missive
          </button>
        </div>
      </div>
    </Modal>
  );
}
